import { useCallback } from 'react';
import { EAttributeType } from '../../../@types/AttributeTypes';
import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import { IWall } from '../../../models/abstract/Wall';
import AttributeModel from '../../../models/standard/AttributeModel';
import PartitionWallModel from '../../../models/standard/PartitionWallModel';
import StandardWallModel from '../../../models/standard/StandardWallModel';
import WindowModel from '../../../models/standard/WindowModel';
import { DefaultAttributeId, getDefaultAttributesForObject, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';

export interface IWindowActions {
  deleteWindow: (wallIndex: number, windowIndex: number) => void;
  updateWindowWidth: (wallIndex: number, windowIndex: number, newWidth: number) => void;
  updateWindowAttributes: (wallIndex: number, windowIndex: number, attributes: AttributeModel[]) => void;
  setWindowWidthAttribute: (wallIndex: number, windowIndex: number, width: number, widthUnit: string | null) => void;
}

export const useWindowActions = (setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>): IWindowActions => {
  
  // --- HELPER: Reconstructs either wall type with updated windows ---
  const recreateWallWithWindows = (originalWall: IWall, newWindows: WindowModel[]): IWall | null => {
    if (originalWall instanceof StandardWallModel) {
      const radsA = [...originalWall.sides.A.radiators];
      const radsB = [...originalWall.sides.B.radiators];

      const newWall = new StandardWallModel(
        originalWall.startPoint,
        originalWall.endPoint,
        originalWall.depth,
        newWindows,
        originalWall.doors,
        [], // Legacy radiators (empty)
        originalWall.attributes,
        originalWall.uuid,
        originalWall.passages,
      );

      // Restore radiators
      newWall.sides.A.radiators = radsA;
      newWall.sides.B.radiators = radsB;
      newWall.updateWallLengths();
      return newWall;
    }

    if (originalWall instanceof PartitionWallModel) {
      const newWall = new PartitionWallModel(originalWall.startPoint, originalWall.endPoint);
      newWall.depth = originalWall.depth;
      newWall.doors = originalWall.doors;
      newWall.windows = newWindows;
      newWall.passages = originalWall.passages;
      newWall.sides = originalWall.sides; // Preserve radiators/sides
      newWall.attributes = originalWall.attributes;
      newWall.setUUID(originalWall.uuid);

      if (newWall.updateWallLengths) {
        newWall.updateWallLengths();
      }
      return newWall;
    }

    return null;
  };

  const deleteWindow = useCallback(
    (wallIndex: number, windowIndex: number): void => {
      setBuilding((prevBuilding) => {
        const walls = [...prevBuilding.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.windows) return prevBuilding;

        const windows = [...currentWall.windows];
        windows.splice(windowIndex, 1);

        const newWall = recreateWallWithWindows(currentWall, windows);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prevBuilding, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.WINDOW_DELETED };
        }
        return prevBuilding;
      });
    },
    [setBuilding],
  );

  const updateWindowWidth = useCallback(
    (wallIndex: number, windowIndex: number, newWidth: number): void => {
      setBuilding((prevBuilding) => {
        const walls = [...prevBuilding.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.windows) return prevBuilding;

        const windows = [...currentWall.windows];
        const currentWindow = windows[windowIndex];

        const updatedWindow = new WindowModel(currentWindow.position, newWidth, currentWindow.depth, currentWindow.angle, currentWindow.attributes);
        updatedWindow.setUUID(currentWindow.uuid);
        windows[windowIndex] = updatedWindow;

        const newWall = recreateWallWithWindows(currentWall, windows);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prevBuilding, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.WINDOW_WIDTH_CHANGED };
        }
        return prevBuilding;
      });
    },
    [setBuilding],
  );

  const updateWindowAttributes = useCallback(
    (wallIndex: number, windowIndex: number, attributes: AttributeModel[]): void => {
      setBuilding((prevBuilding) => {
        const walls = [...prevBuilding.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.windows) return prevBuilding;

        const windows = [...currentWall.windows];
        const currentWindow = windows[windowIndex];

        const defaultWindowAttrs = getDefaultAttributesForObject(ObjectType.Window);
        const updatedAttrs = attributes.map((attr) => {
          const defaultAttr = defaultWindowAttrs.find((def) => def.id === attr.id);
          return new AttributeModel(
            attr.id,
            attr.key,
            attr.value,
            attr.predecessorId,
            attr.type,
            attr.unit,
            attr.hidden ?? defaultAttr?.hidden ?? false,
            attr.forced ?? defaultAttr?.forced ?? false,
            attr.ownerId,
            attr.userId,
            attr.publicFlag,
            attr.localization,
            attr.defaults,
            attr.validation,
            attr.unitFamily
          );
        });

        const updatedWindow = new WindowModel(currentWindow.position, currentWindow.length, currentWindow.depth, currentWindow.angle, updatedAttrs);
        updatedWindow.setUUID(currentWindow.uuid);
        windows[windowIndex] = updatedWindow;

        const newWall = recreateWallWithWindows(currentWall, windows);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prevBuilding, walls };
        }
        return prevBuilding;
      });
    },
    [setBuilding],
  );

  const setWindowWidthAttribute = useCallback(
    (wallIndex: number, windowIndex: number, width: number, widthUnit: string | null): void => {
      setBuilding((prevBuilding) => {
        const walls = [...prevBuilding.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.windows) return prevBuilding;

        const windows = [...currentWall.windows];
        const currentWindow = windows[windowIndex];

        if (!currentWindow.attributes?.some((attr) => attr.id === DefaultAttributeId.WINDOW_WIDTH)) {
          return prevBuilding;
        }

        const updatedAttrs = currentWindow.attributes.map((attr) => {
          if (attr.id === DefaultAttributeId.WINDOW_WIDTH) {
            return new AttributeModel(
              attr.id,
              attr.key,
              width,
              attr.predecessorId,
              EAttributeType.NUMBER,
              widthUnit,
              attr.hidden,
              attr.forced,
              attr.ownerId,
              attr.userId,
              attr.publicFlag,
              attr.localization,
              attr.defaults,
              attr.validation,
              attr.unitFamily
            );
          }
          return attr;
        });

        const updatedWindow = new WindowModel(currentWindow.position, currentWindow.length, currentWindow.depth, currentWindow.angle, updatedAttrs);
        updatedWindow.setUUID(currentWindow.uuid);
        windows[windowIndex] = updatedWindow;

        const newWall = recreateWallWithWindows(currentWall, windows);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prevBuilding, walls };
        }
        return prevBuilding;
      });
    },
    [setBuilding],
  );

  return {
    deleteWindow,
    updateWindowWidth,
    updateWindowAttributes,
    setWindowWidthAttribute,
  };
};
